import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, ChevronRight, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const formations = [
    { href: '/', label: 'IA & Automatisation', description: 'Intelligence artificielle pour PME' },
    { href: '/management-leadership', label: 'Management & Leadership', description: "L'art de fédérer" },
    { href: '/soft-skills', label: 'Soft Skills', description: 'Intelligence relationnelle' },
    { href: '/digital-web', label: 'Digital & Web', description: 'Empreinte numérique' },
  ];

  const navLinks = [
    { href: '/#services', label: 'Services' },
    { href: '/#methodologie', label: 'Méthodologie' },
    { href: '/#offres', label: 'Offres' },
    { href: '/#temoignages', label: 'Témoignages' },
    { href: '/simulation-roi', label: 'Simulation ROI', isPage: true },
    { href: '/#diagnostic', label: 'Contact' },
  ];

  const isHomePage = location.pathname === '/';

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100" data-testid="header">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            <span className="font-bold text-gray-900 hidden sm:block" style={{ fontSize: '1.2rem' }}>
              ACDC-Formation
            </span>
            <span className="font-bold text-gray-900 sm:hidden" style={{ fontSize: '1.2rem' }}>
              ACDC-Formation
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {/* Formations Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-blue-700 transition-colors">
                Formations
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                {formations.map((formation) => (
                  <DropdownMenuItem key={formation.href} asChild>
                    <Link 
                      to={formation.href} 
                      className="flex flex-col gap-1 p-3"
                    >
                      <span className="font-medium text-gray-900">{formation.label}</span>
                      <span className="text-xs text-gray-500">{formation.description}</span>
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Other nav links */}
            {navLinks.map((link) => (
              link.isPage ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-sm font-medium text-gray-600 hover:text-blue-700 transition-colors"
                  data-testid={`nav-link-${link.href.replace('/', '')}`}
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={isHomePage ? link.href : `/${link.href}`}
                  className="text-sm font-medium text-gray-600 hover:text-blue-700 transition-colors"
                  data-testid={`nav-link-${link.href.replace('#', '')}`}
                >
                  {link.label}
                </a>
              )
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <Link to="/admin" data-testid="header-admin-link">
              <Button variant="ghost" size="sm" className="text-gray-600">
                Admin
              </Button>
            </Link>
            <Link to="/#diagnostic" data-testid="header-cta">
              <Button
                size="sm"
                className="text-white rounded-full transition-all duration-300 ease-out hover:-translate-y-0.5"
                style={{ 
                  backgroundColor: '#1C2E4A',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#0F1E33';
                  e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.12)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#1C2E4A';
                  e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                }}
              >
                Diagnostic gratuit
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-gray-600 hover:text-gray-900"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={isMobileMenuOpen}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            className="lg:hidden py-4 border-t border-gray-100"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1">
              {/* Formations section */}
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Formations
              </div>
              {formations.map((formation) => (
                <Link
                  key={formation.href}
                  to={formation.href}
                  className="px-4 py-3 text-gray-700 hover:text-blue-700 hover:bg-gray-50 rounded-lg transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {formation.label}
                </Link>
              ))}
              
              <hr className="my-2 border-gray-100" />
              
              {/* Other links */}
              {navLinks.map((link) => (
                link.isPage ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="px-4 py-3 text-gray-600 hover:text-blue-700 hover:bg-gray-50 rounded-lg transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.href}
                    href={isHomePage ? link.href : `/${link.href}`}
                    className="px-4 py-3 text-gray-600 hover:text-blue-700 hover:bg-gray-50 rounded-lg transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.label}
                  </a>
                )
              ))}
              
              <hr className="my-2 border-gray-100" />
              
              <Link
                to="/admin"
                className="px-4 py-3 text-gray-600 hover:text-blue-700 hover:bg-gray-50 rounded-lg"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Admin
              </Link>
              
              <div className="px-4 pt-2 space-y-2">
                <Link to="/#diagnostic" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button 
                    className="w-full text-white rounded-full transition-all duration-300"
                    style={{ backgroundColor: '#1C2E4A' }}
                  >
                    Demander un diagnostic stratégique
                  </Button>
                </Link>
                <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button variant="outline" className="w-full rounded-full">
                    Être rappelé
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
